import React, { useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faSync } from "@fortawesome/free-solid-svg-icons"
import {
	postDiagnostique,
	deleteDiagnostique,
	postDiagTarif,
	deleteDiagTarif,
} from "../../../store/actions/diagnostiqueActions"
import { COLORS } from "../../../utilities/Constants"
import { generalStyles as gs } from "../../styles/styles"
import CustomModal from "../../basics/interface/CustomModal"

const ACTIONS = [
	{ key: "aucune", label: "Aucune (dépannage standard)" },
	{ key: "is_sur_place", label: "Sur place (réparé, pas de transport)" },
	{ key: "is_a_vide", label: "Course à vide" },
	{ key: "is_annule", label: "Dépannage annulé" },
]

const styles = {
	sousTitre: {
		fontSize: 13,
		fontWeight: 600,
		color: COLORS.textMuted,
		margin: "18px 0 8px",
		borderBottom: `1px solid ${COLORS.border}`,
		paddingBottom: 6,
	},
	radioRow: {
		display: "flex",
		alignItems: "center",
		gap: 8,
		fontSize: 13,
		color: COLORS.text,
		padding: "3px 0",
		cursor: "pointer",
	},
	tarifRow: {
		display: "flex",
		alignItems: "center",
		gap: 8,
		marginBottom: 8,
	},
	// En-têtes du tableau des tarifs — mêmes largeurs que les cellules des
	// lignes (tarifInput / flex 1 / tarifActions) pour rester alignés.
	tarifHeaderRow: {
		display: "flex",
		alignItems: "center",
		gap: 8,
		marginBottom: 4,
		fontSize: 11,
		fontWeight: 600,
		color: COLORS.textLight,
		textTransform: "uppercase",
		letterSpacing: "0.06em",
	},
	// `gs.input` pose flex:1 — les colonnes montant doivent le neutraliser
	// (flex 0 0) pour garder une largeur fixe.
	tarifInput: { width: 84, flex: "0 0 84px" },
	// Cellules d'en-tête : paddingLeft = padding+bordure des inputs (11+1)
	// pour que les libellés tombent au-dessus du texte des champs.
	thMontant: {
		width: 84,
		flex: "0 0 84px",
		boxSizing: "border-box",
		paddingLeft: 12,
	},
	thFlex: { flex: 1, minWidth: 0, boxSizing: "border-box", paddingLeft: 12 },
	tarifActions: {
		width: 150,
		display: "flex",
		gap: 8,
		justifyContent: "flex-end",
		flexShrink: 0,
	},
	smallBtn: {
		border: `1px solid ${COLORS.border}`,
		background: COLORS.surface,
		borderRadius: 8,
		padding: "6px 10px",
		fontSize: 12,
		cursor: "pointer",
		color: COLORS.text,
	},
	smallBtnDanger: {
		border: `1px solid ${COLORS.dangerBorder}`,
		background: COLORS.dangerLight,
		borderRadius: 8,
		padding: "6px 10px",
		fontSize: 12,
		cursor: "pointer",
		color: COLORS.danger,
	},
	hint: { fontSize: 12, color: COLORS.textLight, marginTop: 4 },
}

// Ligne d'édition d'un tarif d'assistance (existante ou nouvelle).
const TarifAssistanceRow = ({
	assistances,
	entitesPrises,
	initial,
	diagId,
	isNew,
}) => {
	const dispatch = useDispatch()
	const [entiteId, setEntiteId] = useState(initial.entiteId || "")
	const [tarif, setTarif] = useState(initial.tarif ?? "")
	const [tarifAutre, setTarifAutre] = useState(initial.tarif_autre ?? "")
	const [texteAutre, setTexteAutre] = useState(initial.texte_autre || "")
	const [busy, setBusy] = useState(false)
	const [confirmDelete, setConfirmDelete] = useState(false)

	const options = isNew
		? assistances.filter((a) => !entitesPrises.includes(String(a.id)))
		: assistances

	const save = () => {
		if (!entiteId) return
		setBusy(true)
		dispatch(
			postDiagTarif(
				JSON.stringify({
					diag_id: diagId,
					entite_id: entiteId,
					tarif: parseFloat(tarif) || 0,
					tarif_autre: parseFloat(tarifAutre) || 0,
					texte_autre: texteAutre.trim(),
				}),
			),
		).finally(() => {
			setBusy(false)
			if (isNew) {
				setEntiteId("")
				setTarif("")
				setTarifAutre("")
				setTexteAutre("")
			}
		})
	}
	const remove = () => {
		if (!confirmDelete) {
			setConfirmDelete(true)
			return
		}
		setBusy(true)
		dispatch(
			deleteDiagTarif(
				JSON.stringify({ diag_id: diagId, entite_id: entiteId }),
			),
		).finally(() => setBusy(false))
	}

	return (
		<div style={styles.tarifRow}>
			<select
				style={{ ...gs.input, flex: 1, minWidth: 0 }}
				value={entiteId}
				disabled={!isNew}
				onChange={(e) => setEntiteId(e.target.value)}
			>
				<option value="">— Assistance —</option>
				{options.map((a) => (
					<option key={a.id} value={a.id}>
						{a.label}
					</option>
				))}
			</select>
			<input
				style={{ ...gs.input, ...styles.tarifInput }}
				type="number"
				value={tarif}
				onChange={(e) => setTarif(e.target.value)}
			/>
			<input
				style={{ ...gs.input, ...styles.tarifInput }}
				type="number"
				value={tarifAutre}
				onChange={(e) => setTarifAutre(e.target.value)}
			/>
			<input
				style={{ ...gs.input, flex: 1, minWidth: 0 }}
				value={texteAutre}
				onChange={(e) => setTexteAutre(e.target.value)}
			/>
			<span style={styles.tarifActions}>
				<button
					style={styles.smallBtn}
					onClick={save}
					disabled={busy || !entiteId}
				>
					{busy ? (
						<FontAwesomeIcon icon={faSync} spin />
					) : isNew ? (
						"Ajouter"
					) : (
						"Enregistrer"
					)}
				</button>
				{!isNew && (
					<button
						style={styles.smallBtnDanger}
						onClick={remove}
						disabled={busy}
					>
						{confirmDelete ? "Confirmer ?" : "✕"}
					</button>
				)}
			</span>
		</div>
	)
}

// Édition d'un nœud de l'arbre des diagnostics (modal V2).
// Une feuille porte : une action terrain (sur place / à vide / annulé —
// uniques, le backend les retire d'ailleurs), l'ouverture des articles, les
// tarifs négociés par assistance, et le tarif de base = repli quand
// l'assistance de la mission n'a pas de tarif configuré.
const DiagnostiqueModalV2 = ({ diag, onClose }) => {
	const dispatch = useDispatch()
	const tree = useSelector((s) => s.diagnostiqueInfo.tree)
	const tarifs = useSelector((s) => s.diagnostiqueInfo.tarifs)
	const assistances = useSelector((s) => s.autreInfo.assistances) || []
	const isCreate = !diag.id

	const [nom, setNom] = useState(diag.nom || "")
	const [parent, setParent] = useState(
		diag.parent === null || diag.parent === undefined ? "" : diag.parent,
	)
	const [isLeaf, setIsLeaf] = useState(!!diag.is_leaf)
	const [action, setAction] = useState(
		diag.is_sur_place
			? "is_sur_place"
			: diag.is_a_vide
				? "is_a_vide"
				: diag.is_annule
					? "is_annule"
					: "aucune",
	)
	const [showArticles, setShowArticles] = useState(!!diag.show_articles)
	const [isMenu, setIsMenu] = useState(!!diag.is_menu)
	const [menuIndex, setMenuIndex] = useState(diag.menu_index ?? 0)
	const [tarifBase, setTarifBase] = useState(diag.tarif_base ?? "")
	const [tarifBaseAutre, setTarifBaseAutre] = useState(
		diag.tarif_base_autre ?? "",
	)
	const [texteBaseAutre, setTexteBaseAutre] = useState(
		diag.texte_base_autre || "",
	)
	const [confirmDelete, setConfirmDelete] = useState(false)
	const [busy, setBusy] = useState(false)

	// Parents possibles : les catégories (non-feuilles), sauf soi-même.
	const categories = Object.keys(tree)
		.filter((k) => k !== "-1")
		.flatMap((k) => tree[k])
		.filter((d) => !d.is_leaf && d.id !== diag.id)
	const tarifsDuDiag = tarifs[diag.id] || {}
	const entitesPrises = Object.keys(tarifsDuDiag)

	const invalid = !nom.trim()

	const save = () => {
		setBusy(true)
		const payload = {
			id: diag.id || null,
			nom: nom.trim(),
			parent: parent === "" ? null : parseInt(parent, 10),
			is_leaf: isLeaf,
			is_sur_place: isLeaf && action === "is_sur_place",
			is_a_vide: isLeaf && action === "is_a_vide",
			is_annule: isLeaf && action === "is_annule",
			show_articles: isLeaf && showArticles,
			is_menu: isMenu,
			menu_index: parseInt(menuIndex, 10) || 0,
			tarif_base: isLeaf ? parseFloat(tarifBase) || 0 : 0,
			tarif_base_autre: isLeaf ? parseFloat(tarifBaseAutre) || 0 : 0,
			texte_base_autre: isLeaf ? texteBaseAutre : "",
		}
		dispatch(postDiagnostique(JSON.stringify(payload)))
			.then(() => onClose())
			.catch(() => setBusy(false))
	}
	const remove = () => {
		if (!confirmDelete) {
			setConfirmDelete(true)
			return
		}
		setBusy(true)
		dispatch(deleteDiagnostique(JSON.stringify({ id: diag.id })))
			.then(() => onClose())
			.catch(() => {
				setBusy(false)
				setConfirmDelete(false)
			})
	}

	const buttons = []
	if (!isCreate) {
		buttons.push({
			title: confirmDelete ? "Confirmer ?" : "Supprimer",
			styleName: "danger",
			onClick: remove,
			disabled: busy,
		})
	}
	buttons.push({
		title: "Annuler",
		styleName: "secondary",
		onClick: onClose,
		disabled: busy,
	})
	buttons.push({
		title: busy ? <FontAwesomeIcon icon={faSync} spin /> : "Enregistrer",
		styleName: "primary",
		onClick: save,
		disabled: busy || invalid,
	})

	return (
		<CustomModal
			title={isCreate ? "Nouveau diagnostic" : "Éditer le diagnostic"}
			onClose={busy ? () => {} : onClose}
			showCloseButton={!busy}
			buttons={buttons}
			maxWidth={780}
		>
			<div style={gs.fieldGroup}>
				<div style={gs.fieldRow}>
					<label style={gs.label}>
						Nom<span style={{ color: COLORS.danger }}> *</span>
					</label>
					<input
						style={gs.input}
						value={nom}
						onChange={(e) => setNom(e.target.value)}
						autoFocus
					/>
				</div>
				<div style={gs.fieldRow}>
					<label style={gs.label}>Catégorie parente</label>
					<select
						style={gs.input}
						value={parent}
						onChange={(e) => setParent(e.target.value)}
					>
						<option value="">— Racine —</option>
						{categories.map((c) => (
							<option key={c.id} value={c.id}>
								{c.nom}
							</option>
						))}
					</select>
				</div>
				<div style={gs.fieldRow}>
					<label style={styles.radioRow}>
						<input
							type="checkbox"
							checked={isLeaf}
							onChange={(e) => setIsLeaf(e.target.checked)}
						/>
						Feuille (sélectionnable par le dépanneur)
					</label>
					<label style={styles.radioRow}>
						<input
							type="checkbox"
							checked={isMenu}
							onChange={(e) => setIsMenu(e.target.checked)}
						/>
						Menu rapide
					</label>
					{isMenu && (
						<input
							style={{ ...gs.input, width: 70 }}
							type="number"
							title="Ordre dans le menu rapide"
							value={menuIndex}
							onChange={(e) => setMenuIndex(e.target.value)}
						/>
					)}
				</div>
			</div>

			{isLeaf && (
				<>
					<div style={styles.sousTitre}>Action terrain</div>
					{ACTIONS.map((a) => (
						<label key={a.key} style={styles.radioRow}>
							<input
								type="radio"
								name="action"
								checked={action === a.key}
								onChange={() => setAction(a.key)}
							/>
							{a.label}
						</label>
					))}
					<label style={{ ...styles.radioRow, marginTop: 6 }}>
						<input
							type="checkbox"
							checked={showArticles}
							onChange={(e) => setShowArticles(e.target.checked)}
						/>
						Ouvre la saisie d'articles (batteries…)
					</label>

					<div style={styles.sousTitre}>Tarif de base</div>
					<div style={styles.tarifHeaderRow}>
						<span style={styles.thMontant}>Tarif</span>
						<span style={styles.thMontant}>Tarif autre</span>
						<span style={styles.thFlex}>Libellé autre</span>
					</div>
					<div style={styles.tarifRow}>
						<input
							style={{ ...gs.input, ...styles.tarifInput }}
							type="number"
							value={tarifBase}
							onChange={(e) => setTarifBase(e.target.value)}
						/>
						<input
							style={{ ...gs.input, ...styles.tarifInput }}
							type="number"
							value={tarifBaseAutre}
							onChange={(e) => setTarifBaseAutre(e.target.value)}
						/>
						<input
							style={{ ...gs.input, flex: 1, minWidth: 0 }}
							value={texteBaseAutre}
							onChange={(e) => setTexteBaseAutre(e.target.value)}
						/>
					</div>
					<div style={styles.hint}>
						Appliqué en repli quand l'assistance de la mission n'a
						pas de tarif configuré ci-dessous.
					</div>

					<div style={styles.sousTitre}>Tarifs par assistance</div>
					{isCreate ? (
						<div style={styles.hint}>
							Enregistrez d'abord le diagnostic pour saisir les
							tarifs par assistance.
						</div>
					) : (
						<>
							<div style={styles.tarifHeaderRow}>
								<span style={styles.thFlex}>Assistance</span>
								<span style={styles.thMontant}>Tarif</span>
								<span style={styles.thMontant}>
									Tarif autre
								</span>
								<span style={styles.thFlex}>Libellé autre</span>
								<span style={styles.tarifActions} />
							</div>
							{entitesPrises.map((entiteId) => (
								<TarifAssistanceRow
									key={entiteId}
									diagId={diag.id}
									assistances={assistances}
									entitesPrises={entitesPrises}
									isNew={false}
									initial={{
										entiteId,
										...tarifsDuDiag[entiteId],
									}}
								/>
							))}
							<TarifAssistanceRow
								diagId={diag.id}
								assistances={assistances}
								entitesPrises={entitesPrises}
								isNew={true}
								initial={{}}
							/>
						</>
					)}
				</>
			)}
		</CustomModal>
	)
}

export default DiagnostiqueModalV2
